import {FC, useContext, useEffect, useState} from 'react'
import {ILanguage} from '../types/common'
import FundTable from '../common/FundTable'
import {Fund} from '../types/Fund'
import i18n from 'i18next'
import axios from 'axios'
import {FundCatalog} from '../types/FundCatalog'
import {OfeFundCatalogContext} from './ctx/OfeFundCatalogContext'
import LineChart from '../common/LineChart'
import {ChartData} from '../types/ChartData'
import {Periods} from '../types/Periods'
import {format} from 'date-fns'

const LayoutOFE: FC<{
    language: ILanguage
}> = ({language}) => {
    const [tableData, setTableData] = useState<Fund[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [fundList, setFundList] = useState<string>('')
    const [chartData, setChartData] = useState<ChartData>({
        begin: '',
        end: '',
        currency: [],
        relativePeriods: {} as Periods, // Initialize with appropriate value
        valuation_dates: [],
        data: [],
    })
    const localCtx: any = useContext(OfeFundCatalogContext)
    const dynamicColumns: object = {
        oneDay: {value: '1D', label: '1D'},
        oneMonth: {value: '1M', label: '1M'},
        threeMonths: {value: '3M', label: '3M'},
        sixMonths: {value: '6M', label: '6M'},
        oneYear: {value: '12M', label: '12M'},
        threeYears: {value: '36M', label: '36M'},
        fiveYears: {value: '60M', label: '60M'},
        yearToDate: {value: 'YTD', label: 'YTD'},
        max: {value: 'MAX', label: 'MAX'},
    }

    useEffect(() => {
        const params = new URLSearchParams()
        if (localCtx.date) {
            params.append('date', format(localCtx.date, 'yyyy-MM-dd'))
        }
        axios
            .get<FundCatalog>(
                `${process.env.REACT_APP_API_BASE_URL}/api/fund?kind=ofe${params.size || [...params.keys()].length ? '&' + params.toString() : ''}`,
                {
                    headers: {
                        Accept: 'application/json',
                    },
                    auth: {
                        username: `${process.env.REACT_APP_API_USERNAME}`,
                        password: `${process.env.REACT_APP_API_PASSWORD}`,
                    },
                }
            )
            .then((result) => {
                const today: Date = new Date(result.data.today)
                let begin: Date = new Date(2100, 0, 1)
                let end: Date = new Date(1900, 0, 1)
                const items: Fund[] = result.data.items
                const holiday = result.data.holiday.map((item) => {
                    return new Date(item)
                })

                items.forEach((item) => {
                    if (begin > new Date(item.begin)) {
                        begin = new Date(item.begin)
                    }
                })

                items.forEach((item) => {
                    if (end < new Date(item.end)) {
                        end = new Date(item.end)
                    }
                })

                localCtx.setMinDate(begin)
                localCtx.setMaxDate(end)
                localCtx.setToday(today)
                localCtx.setHoliday(holiday)

                setTableData(items)
                const ids = items.map((item) => item.id).join(',')
                setFundList(ids)
                localCtx.setChartItems(ids.split(','))
                setIsLoading(false)
            })
            .catch((error) => {
                console.error(error)
            })
    }, [localCtx.date])

    useEffect(() => {
        i18n.changeLanguage(language)
    }, [language])

    useEffect(() => {
        if (fundList.length > 0) {
            axios
                .get<ChartData>(`${process.env.REACT_APP_API_BASE_URL}/api/fund/compare/${fundList}`, {
                    headers: {
                        Accept: 'application/json',
                    },
                    auth: {
                        username: `${process.env.REACT_APP_API_USERNAME}`,
                        password: `${process.env.REACT_APP_API_PASSWORD}`,
                    },
                })
                .then((result) => {
                    setChartData(result.data)
                })
                .catch((error) => {
                    console.error(error)
                })
        }
    }, [language, fundList])

    return (
        <>
            <section id="fund-list">
                <div className="rwdShutter">
                    <div className="navOfflineBlock">
                        <div className="navOfflineLogo"></div>
                    </div>
                </div>
            </section>

            <div className={'content'}>
                <FundTable
                    mode={'none'}
                    language={language}
                    data={tableData}
                    isLoading={isLoading}
                    ctx={localCtx}
                    dynamicColumns={dynamicColumns}
                />
                {chartData.data.length > 0 && (
                    <LineChart
                        chartData={chartData}
                        language={language}
                        chartItems={localCtx.chartItems}
                        setChartItems={localCtx.setChartItems}
                        chartMode="catalog"
                        ctx={localCtx}
                        sortedComparedRows={tableData}
                        selectedRateOfReturnOptions={localCtx.columns}
                    />
                )}
            </div>
        </>
    )
}

export default LayoutOFE
